import * as React from 'react'
import { createPortal } from 'react-dom'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import MonthPicker from '@/modules/drivers/shared/date/MonthPicker'
import { FaIcon, faCalendar } from '@/lib/fa'
import Tooltip from '@/modules/drivers/shared/ToolTip'
import type { BillingPeriod } from '../types'

const PICKER_HEIGHT = 332
const PICKER_WIDTH = 340
const VIEWPORT_MARGIN = 8

interface BillingPeriodPickerProps {
  period: BillingPeriod
  onChangePeriod: (period: BillingPeriod) => void
}

const BillingPeriodPicker: React.FC<BillingPeriodPickerProps> = ({
  period,
  onChangePeriod,
}) => {
  const { t } = useLocalize()
  const [pickerOpen, setPickerOpen] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const popupRef = React.useRef<HTMLDivElement>(null)
  const [pickerPos, setPickerPos] = React.useState({ top: 0, left: 0 })

  const openPicker = () => {
    const btn = buttonRef.current
    if (!btn) {
      setPickerOpen(true)
      return
    }
    const rect = btn.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const top =
      spaceBelow < PICKER_HEIGHT
        ? rect.top + window.scrollY - PICKER_HEIGHT - 4
        : rect.bottom + window.scrollY + 4
    const maxLeft =
      window.innerWidth - PICKER_WIDTH - VIEWPORT_MARGIN + window.scrollX
    const minLeft = VIEWPORT_MARGIN + window.scrollX
    const left = Math.max(
      minLeft,
      Math.min(rect.left + window.scrollX, maxLeft),
    )
    setPickerPos({ top, left })
    setPickerOpen(true)
  }

  React.useEffect(() => {
    if (!pickerOpen) return
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (containerRef.current?.contains(target)) return
      if (popupRef.current?.contains(target)) return
      setPickerOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [pickerOpen])

  return (
    <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
      <Tooltip text={t('billing.tip.billingPeriod')} asChild portal maxWidth="22rem">
        <p className="tw-color-content-base tw-font-sm tw-cursor-help">
          {t('billing.billingPeriod')}:
        </p>
      </Tooltip>
      <div ref={containerRef} className="tw-relative tw-inline-block">
        <button
          ref={buttonRef}
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            if (pickerOpen) setPickerOpen(false)
            else openPicker()
          }}
          className={`tw-flex tw-items-center tw-gap-2 tw-rounded-sm tw-border tw-border-base-300 tw-bg-transparent tw-px-2 tw-py-1 hover:tw-bg-base-200 ${pickerOpen ? 'tw-border-2 tw-border-primary' : ''}`}
        >
          <FaIcon icon={faCalendar} className="tw-h-3.5 tw-w-3.5 tw-text-content-base" />
          <span className="tw-text-xs tw-font-medium tw-text-content-base">{period}</span>
        </button>
        {pickerOpen &&
          createPortal(
            <div
              ref={popupRef}
              className="tw-absolute tw-z-[9999]"
              style={{ top: pickerPos.top, left: pickerPos.left }}
            >
              <MonthPicker
                value={period}
                onConfirm={(v) => {
                  onChangePeriod(v)
                  setPickerOpen(false)
                }}
              />
            </div>,
            document.body,
          )}
      </div>
    </div>
  )
}

export default BillingPeriodPicker
